import React, { useRef, useEffect, useMemo } from 'react';
import { NavLink } from 'react-router-dom';
import axios from 'axios';
import styled from 'styled-components';
import LabeledFrame from 'components/LabeledFrame';
import EmpoweredSelector from 'components/EmpoweredSelector';
import { getCommunity } from 'helpers/community';
import { formatNumber } from 'helpers/generic';
import { openPopupWindow } from 'helpers/generic';

const Table = styled.table`
	width: 100%;
	
	td,th {
		font-size: 12px;
		line-height: 12px;

		&:nth-child(1) {

		}
		&:nth-child(2) {
			width: 10%;
			position: relative;

			input {
				position: absolute;
				top: 0;
				left: 0;
				width: 100%;
				height: 100%;
				text-align: center;
				border: 0;
			}
		}
		&:nth-child(3) {
			width: 35px;

			button {
				display: block;
				width: 100%;
				color: var(--bs-red);
				font-size: 14px;
				background: transparent;
				border: none;
			}
		}
	}

	th {
		font-weight: 600;
		padding: 8px;
		white-space: nowrap;
	}

	td {
		&:first-of-type {
			padding: 0;

			.empowered-selector {

				.empowered-selector_label {
					border: 0;

					&:not(.disabled) {
						background: transparent;
					}
				}

				.empowered-selector_menu {
					box-shadow: 0px 2px 5px var(--bs-gray-300);
				}
			}
		}

		&.has-errors {
			background: var(--bs-danger);

			.empowered-selector_label,
			input[type=number] {
				color: white;
			}
		}
	}
`;

const OwnerRow = styled.tr`
	
`;

let axiosCancelToken = null;

export default function Owners(props) {
	let owners = useMemo(() => props.owners ?? [], [props.owners]);
	let setOwners = props.setOwners;
	let errors = props.errors ?? [];
	let readOnly = props.readOnly ?? false;

	let lastRow = useRef(null);
	let lastRowNumber = useRef(null);

	useEffect(() => {
		axiosCancelToken = axios.CancelToken.source();
	
		return function cleanup() {
           	axiosCancelToken.cancel();
        }
	}, []);

	useEffect(() => {
		if ( lastRow.current ) {
			lastRow.current.focus();
			lastRow.current.select();
			lastRow.current = null;
		}
	}, [owners]);
 
	const addOwner = () => {
		let ows = [...owners];
		ows.push({
			id: null,
			name: null,
			percent: ''
		});
		setOwners(ows);

		lastRowNumber.current = ows.length - 1; // Needed to focus
	}

	const removeOwner = (idx) => {
		let c = window.confirm('¿Seguro que quieres eliminar este propietario?');
		if ( !c ) return false;

		let ows = [...owners];
		ows.splice(idx, 1);
		setOwners(ows);
	}

	const loadOwners = (input, callback) => {
		axios.get('/api/manager/owners/list', {
			params: {
				search: input,
				no_paginate: true,
				community_id: getCommunity()?.id,
				except_ids: owners.map(el => el.id)
			},
			cancelToken: axiosCancelToken.token
		}).then((response) => {
			let formatted = response.data.map((el) => {
				return {
					value: el, 
					label: <div >
								<div>{el.name}</div>
								{ el.vatnumber &&
									<small className="d-inline-block mt-1 text-secondary">{el.vatnumber}</small>
								}
						   </div>
				};
			});
			callback(formatted);
	  	}).catch((error) => {
			if ( axios.isCancel(error) ) return;
		});	
	}

	const selectOwner = (idx, data) => {
		let ows = [...owners];
		ows[idx].id = data?.id;
		ows[idx].name = data?.name;
		setOwners(ows);
	}

	const setPercent = (idx, value) => {
		let ows = [...owners];
		value = parseFloat(value);
		if ( isNaN(value) ) value = '';
		ows[idx].percent = value;
		setOwners(ows);
	}

	const openOwnerWindow = (e) => {
		e.preventDefault();

		openPopupWindow(e.currentTarget.href);
	}

	const openOwnerWindowMouseDownFix = (e) => {
		e.preventDefault();
		e.stopPropagation();
		document.activeElement.blur();
	}

	let rows = [];
	owners.forEach((el, idx) => {
		rows.push(
			<OwnerRow key={idx}>
				<td className={errors['owners.' + idx + '.id'] ? 'has-errors' : ''}>
					<EmpoweredSelector
						load={(input, callback) => loadOwners(input, callback)}
						onChange={(value) => selectOwner(idx, value)}
						timeout={250}
						label={
							<div>
								{el.name ?? ''}&nbsp;
								{el.id &&
									<NavLink className="text-decoration-none text-reset" to={'/owners/edit/' + el.id + '?popup=true'} onClick={(e) => openOwnerWindow(e)} onMouseDown={(e) => openOwnerWindowMouseDownFix(e)}><i className="bi bi-eye text-primary"></i></NavLink>
								}
							</div>
						}
						placeholder="Seleccionar"
						value={el.id}
						disabled={readOnly}
					/>
				</td>
				<td className={errors['owners.' + idx + '.percent'] ? 'has-errors' : ''}>
					<input 
						ref={lastRowNumber.current === idx ? lastRow : null} 
						step="0.01" 
						type="number" 
						className="no-arrows" 
						value={el.percent ?? ''} 
						onChange={(e) => setPercent(idx, e.target.value)} 
						readOnly={readOnly}
					/>
				</td>
				<td>
					<button className={'btn btn-sm p-0 ' + (readOnly ? 'disabled' : '')} onClick={() => removeOwner(idx)}><i className="bi bi-x-circle-fill"></i></button>
				</td>
			</OwnerRow>
		);
	});

	let percent_total = 0;
	owners.forEach(el => percent_total += el.percent);
	percent_total = formatNumber(percent_total, 2);

	return (
		<LabeledFrame 
			className="mb-3" 
			label="Propietarios"
			buttons={
				!readOnly ? 
					<React.Fragment>
						<button className={'btn-unstyled text-primary'} onClick={() => addOwner()}><i className="bi bi-plus-circle-fill"></i></button>
					</React.Fragment>
				:
				null
			}
		>
			{ rows.length > 0 &&
				<React.Fragment>
					<Table className="table table-sm table-bordered mb-0">
						<thead>
							<tr>
								<th>
									<div className="d-flex justify-content-between">
										Propietario

										<NavLink className="text-decoration-none text-primary fw-normal" to={'/owners/add/?popup=true'} onClick={(e) => openOwnerWindow(e)} onMouseDown={(e) => openOwnerWindowMouseDownFix(e)}><i className="bi bi-eye text-primary"></i> Crear ficha</NavLink>
									</div>
								</th>
								<th>% participación</th>
								<th></th>
							</tr>
						</thead>
						<tbody>
							{rows}
						</tbody>
						{rows.length > 0 &&
							<tfoot>
								<tr 
									className={
										'fw-bold ' + 
										(percent_total < 100 ? 'text-secondary' : '') + 
										(percent_total === 100 ? 'text-primary' : '') + 
										(percent_total > 100 ? 'text-danger' : '')
									}
								>
									<td className="text-end pe-2">Total</td>
									<td className="text-center">
										{ percent_total }%
									</td>
									<td></td>
								</tr>
							</tfoot>
						}
					</Table>
					{ percent_total > 100 &&
						<div className="text-danger d-block text-end sbold">
							<i className="bi bi-exclamation-triangle"></i>&nbsp;
							Has superado el límite de participación
						</div>
					}
				</React.Fragment>
			}
		</LabeledFrame>
	);
}